import { Fragment, useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { img1, img2, img3, img4, img5, img6, img7, img8 } from "./shots";

gsap.registerPlugin(ScrollTrigger);

const SHOTS = [
  { id: 1, image: img1.default },
  { id: 2, image: img2.default },
  { id: 3, image: img3.default },
  { id: 4, image: img4.default },
  { id: 5, image: img5.default },
  { id: 6, image: img6.default },
  { id: 7, image: img7.default },
  { id: 8, image: img8.default },
];

const BIO_TEXT =
  "Hi, I'm Peter, I design resilient architectures and solve problems with AI, ensuring your web3 products work flawlessly.";
const MARQUEE_TEXT = "variance from the norm";

export const Hero = () => {
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const carouselWrapperRef = useRef<HTMLDivElement>(null);
  const elevatorWordsRef = useRef<(HTMLSpanElement | null)[]>([]);

  // Marquee Refs
  const marqueeSectionRef = useRef<HTMLDivElement>(null);
  const marqueeTextRef = useRef<HTMLDivElement>(null);
  const infoRowRef = useRef<HTMLDivElement>(null);
  const bordersRef = useRef<(HTMLDivElement | null)[]>([]);

  // Intro Refs
  const word1Ref = useRef<HTMLHeadingElement>(null);
  const word2Ref = useRef<HTMLHeadingElement>(null);

  // Animation State
  const tickerRef = useRef<(() => void) | null>(null);
  const carouselState = useRef({ entryY: 200, opacity: 0 });

  useEffect(() => {
    const ctx = gsap.context(() => {
      const cards = cardsRef.current;

      gsap.set(elevatorWordsRef.current, { y: 50, opacity: 0 });
      gsap.set(bordersRef.current, { scaleX: 0, transformOrigin: "left" });
      gsap.set(marqueeSectionRef.current, { height: 0, autoAlpha: 0 });
      gsap.set(infoRowRef.current, { autoAlpha: 0, y: 10 });

      // --- PHASE 1: MARQUEE LOOP ---
      gsap.to(marqueeTextRef.current, {
        xPercent: -50,
        repeat: -1,
        duration: 40,
        ease: "linear",
      });

      // --- PHASE 2: ENTRY SEQUENCE ---
      const introTl = gsap.timeline();

      introTl
        .to(word1Ref.current, {
          x: "-120vw",
          duration: 1.4,
          ease: "power3.in",
          delay: 0.2,
        })
        .to(
          word2Ref.current,
          { x: "-120vw", duration: 1.4, ease: "power3.in" },
          "<0.15",
        )
        .to(
          overlayRef.current,
          { xPercent: -100, duration: 1.8, ease: "power2.inOut" },
          "<0.2",
        )

        .to(
          elevatorWordsRef.current,
          {
            y: 0,
            opacity: 1,
            duration: 1,
            ease: "back.out(1.7)",
            stagger: 0.02,
          },
          "-=0.5",
        )

        .to(
          bordersRef.current[0]!,
          { scaleX: 1, duration: 0.8, ease: "power3.inOut" },
          "-=0.2",
        )
        .to(
          marqueeSectionRef.current,
          { height: "auto", autoAlpha: 1, duration: 1, ease: "expo.inOut" },
          "-=0.6",
        )
        .to(
          [bordersRef.current[1], bordersRef.current[2]],
          { scaleX: 1, duration: 0.8, ease: "power3.inOut" },
          "<0.5",
        )
        .to(infoRowRef.current, { autoAlpha: 1, y: 0, duration: 0.8 }, "<0.2")

        .to(
          carouselState.current,
          {
            entryY: 0,
            opacity: 1,
            duration: 2.0,
            ease: "power3.out",
          },
          "-=0.8",
        );

      // --- PHASE 3: DRIFT LOOP + DRAG ---
      let xPos = 0;
      let scrollBoost = 0;
      let lastScrollTime = 0;
      let isDragging = false;
      const drift = { speed: 0.5 };
      let resumeTween: gsap.core.Tween | null = null;

      ScrollTrigger.create({
        trigger: containerRef.current,
        start: "top bottom",
        end: "bottom top",
        onUpdate: (self) => {
          if (!isDragging) {
            scrollBoost = Math.min(Math.abs(self.getVelocity()) / 100, 25);
            lastScrollTime = Date.now();
          }
        },
      });

      const wrapper = carouselWrapperRef.current;
      if (wrapper) {
        wrapper.addEventListener("pointerdown", (e: PointerEvent) => {
          e.preventDefault();
          let lastX = e.clientX;
          isDragging = true;

          // Stop drift
          resumeTween?.kill();
          drift.speed = 0;
          document.body.style.cursor = "grabbing";

          // Fresh listeners per drag — no stale state
          const onMove = (ev: PointerEvent) => {
            xPos += ev.clientX - lastX;
            lastX = ev.clientX;
          };

          const onUp = () => {
            isDragging = false;
            document.body.style.cursor = "";
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onUp);
            resumeTween = gsap.to(drift, {
              speed: 0.5,
              duration: 1.2,
              ease: "power2.out",
            });
          };

          window.addEventListener("pointermove", onMove);
          window.addEventListener("pointerup", onUp);
          window.addEventListener("pointercancel", onUp);
        });
      }

      // Cache the easing function once
      const sineInOut = gsap.parseEase("sine.inOut");

      gsap.ticker.add(tick);

      function tick() {
        const timeSinceScroll = Date.now() - lastScrollTime;
        if (timeSinceScroll > 200) {
          scrollBoost *= 0.92; // Smooth decay
        }

        xPos -= isDragging ? drift.speed : drift.speed + scrollBoost;

        const isDesktop = window.innerWidth > 1024;
        const isMobile = window.innerWidth < 768;
        const cardHeight = isDesktop ? 852 : isMobile ? 612 : 732;
        const cardWidth = isDesktop ? 407 : isMobile ? 293 : 350;

        const totalItemWidth = cardWidth + 40;
        const totalWidth = totalItemWidth * SHOTS.length;
        const screenCenter = window.innerWidth / 2;
        const range = window.innerWidth * 0.6;
        const maxWavePush = 100;
        const entryY = carouselState.current.entryY;

        // Apply Opacity
        if (carouselWrapperRef.current) {
          carouselWrapperRef.current.style.opacity =
            carouselState.current.opacity.toString();
        }

        for (let i = 0; i < cards.length; i++) {
          const card = cards[i];
          if (!card) continue;

          let currentX = (i * totalItemWidth + xPos) % totalWidth;
          if (currentX < -cardWidth) currentX += totalWidth;

          const distance = Math.abs(screenCenter - (currentX + cardWidth / 2));
          const waveFactor =
            distance < range ? sineInOut(1 - distance / range) : 0;
          const finalY = maxWavePush * (1 - waveFactor) + entryY;

          // Direct style write — faster than gsap.set per frame
          card.style.transform = `translate3d(${currentX}px, ${finalY}px, 0)`;
          card.style.height = `${cardHeight}px`;
        }
      }
      tickerRef.current = tick;
    }, containerRef);

    return () => {
      ctx.revert();
      if (tickerRef.current) gsap.ticker.remove(tickerRef.current);
    };
  }, []);

  return (
    <Fragment>
      {/* 1. OVERLAY */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-50 dark:bg-neutral-900 bg-neutral-100 flex flex-col justify-end pb-20 px-6 md:px-20 pointer-events-none will-change-transform"
      >
        <div className="flex flex-col items-start leading-none space-y-2 text-foreground">
          <h1
            ref={word1Ref}
            className="text-6xl md:text-7xl lg:text-[10rem] font-medium tracking-tight leading-[0.9]"
          >
            Web3 & AI
          </h1>
          <h1
            ref={word2Ref}
            className="text-6xl md:text-7xl lg:text-[10rem] font-medium tracking-tight leading-[0.9]"
          >
            Engineering
          </h1>
        </div>
      </div>

      {/* 2. HERO SECTION */}
      <section
        ref={containerRef}
        data-section="hero"
        className="w-full bg-background flex flex-col gap-24 z-0 pb-32 overflow-x-hidden"
      >
        {/* TOP CONTENT */}
        <div className="container mx-auto px-6 pt-32 pointer-events-none flex-none">
          {/* BIO */}
          <div className="w-full flex justify-end mb-16">
            <div className="max-w-3xl text-right">
              <p className="text-3xl md:text-4xl leading-tight font-medium text-foreground/90 overflow-hidden">
                {BIO_TEXT.split(" ").map((word, i) => (
                  <span
                    key={i}
                    ref={(el) => {
                      elevatorWordsRef.current[i] = el;
                    }}
                    className="inline-block mr-[8px] will-change-transform"
                  >
                    {word}
                  </span>
                ))}
              </p>
            </div>
          </div>

          {/* MARQUEE */}
          <div className="w-full flex flex-col overflow-hidden">
            <div
              ref={(el) => {
                bordersRef.current[0] = el;
              }}
              className="w-full h-0.5 bg-muted-foreground/40 origin-left"
            ></div>
            <div
              ref={marqueeSectionRef}
              className="overflow-hidden"
              style={{
                maskImage:
                  "linear-gradient(to right, transparent, black 30%, black 70%, transparent)",
                WebkitMaskImage:
                  "linear-gradient(to right, transparent, black 30%, black 70%, transparent)",
              }}
            >
              <div className="py-8 md:py-12 whitespace-nowrap overflow-hidden flex">
                <div ref={marqueeTextRef} className="flex">
                  {[...Array(4)].map((_, i) => (
                    <span
                      key={i}
                      className="text-[8vw] leading-none font-medium tracking-tighter mr-20 lowercase text-foreground"
                    >
                      {MARQUEE_TEXT}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div
              ref={(el) => {
                bordersRef.current[1] = el;
              }}
              className="w-full h-0.5 bg-muted-foreground/40 origin-left"
            ></div>
            <div
              ref={infoRowRef}
              className="flex justify-between items-center py-4 text-xs md:text-lg font-mono text-muted-foreground lowercase tracking-wide"
            >
              <span>
                where i ship from{" "}
                <span className="text-blue-500">&gt;&gt;</span>
              </span>
              <span className="text-foreground/80">
                nairobi, abuja, everywhere
              </span>
            </div>
            <div
              ref={(el) => {
                bordersRef.current[2] = el;
              }}
              className="w-full h-0.5 bg-muted-foreground/40 origin-left"
            ></div>
          </div>
        </div>

        {/* BOTTOM: Mobile Carousel Wrapper */}
        <div
          ref={carouselWrapperRef}
          className="w-full relative mt-auto overflow-hidden h-[596px] cursor-grab select-none touch-pan-y"
          style={{
            maskImage:
              "linear-gradient(to bottom, black 80%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, black 80%, transparent 100%)",
          }}
        >
          <div className="relative w-full h-full pointer-events-none">
            {SHOTS.map((shot, i) => (
              <div
                key={shot.id}
                ref={(el) => {
                  cardsRef.current[i] = el;
                }}
                className="absolute top-0 w-[293px] md:w-[350px] xl:w-[407px] overflow-hidden bg-muted/30 will-change-transform rounded-[40px] md:rounded-[60px] pointer-events-none"
              >
                <div className="w-full h-full relative overflow-hidden">
                  <img
                    src={shot.image}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-linear-to-b from-black/10 to-black/40 pointer-events-none" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Fragment>
  );
};
